import { supabase } from "@/integrations/supabase/client";
import {
  generateRecommendationFromCatalogue,
  type RecommendationOutput,
} from "@shared/recommendation";
import type { BulkCelebrationProgramRow } from "@/lib/bulkCelebrations";
import { mergeBulkDietaries } from "@/lib/bulkCelebrations";
import type { BulkRunMemberRow } from "@/hooks/useBulkCelebrations";

const TEAM_SIZE_HEADCOUNT: Record<string, number> = {
  "1-10": 8,
  "11-25": 18,
  "26-50": 30,
  "51-100": 75,
  "100+": 150,
  team: 8,
  floor: 18,
  office: 30,
};

export async function saveBulkPeriodRecommendation(args: {
  runId: string;
  companyId: string;
  userId: string;
  program: BulkCelebrationProgramRow;
  members: BulkRunMemberRow[];
  recipientLabel: string;
  deliveryDate: string;
  companyTeamSize?: string | null;
}): Promise<{ recommendationId: string; generated: RecommendationOutput }> {
  const celebrationIds = args.members.map((m) => m.celebration_id);
  if (celebrationIds.length === 0) {
    throw new Error("Add at least one person to the bulk roster first.");
  }

  const teamSize =
    (args.companyTeamSize && TEAM_SIZE_HEADCOUNT[args.companyTeamSize]) || 18;
  const tier = args.program.default_tier ?? "standard";

  const generated = await generateRecommendationFromCatalogue(
    {
      tier,
      teamSize,
      dietaries: mergeBulkDietaries(
        args.members.map((m) => {
          const c = m.celebration as {
            team_members?: { dietary_requirements?: string[] };
          } | undefined;
          return {
            dietary_requirements:
              c?.team_members?.dietary_requirements ?? [],
          };
        }),
      ),
      occasion: "birthday",
      date: args.deliveryDate,
      recipientName: args.recipientLabel,
      categoryHint: "cake",
    },
    supabase,
  );

  const lineItems = [
    {
      sku_id: generated.skuId,
      name: generated.productSummary,
      vendor_name: generated.bakery,
      vendor_category: generated.vendorCategory ?? "cake",
      price_aud: generated.totalCents / 100,
      quantity: 1,
    },
  ];

  const { data: rec, error: recErr } = await supabase
    .from("celebration_recommendations")
    .insert({
      celebration_id: celebrationIds[0],
      bulk_run_id: args.runId,
      tier,
      generated_by: "bulk_period",
      bakery_partner_id: null,
      line_items: lineItems,
      total_cents: generated.totalCents,
      delivery_window: { date: args.deliveryDate },
      rationale_text: generated.rationaleDetail,
      status: "approved",
      approved_at: new Date().toISOString(),
      approved_by: args.userId,
    })
    .select("id")
    .single();

  if (recErr || !rec) {
    throw new Error(recErr?.message ?? "Couldn't save bulk recommendation.");
  }

  await supabase
    .from("bulk_celebration_runs")
    .update({
      recommendation_id: rec.id,
      status: "approved",
      updated_at: new Date().toISOString(),
    })
    .eq("id", args.runId);

  await supabase
    .from("celebrations")
    .update({ status: "approved" })
    .in("id", celebrationIds);

  return { recommendationId: rec.id as string, generated };
}
